<template>
  <div class="mb-5">
    <button
      v-if="hasNewUpdated"
      class="new-notice btn btn-sm btn-primary rounded-pill"
      @click="loadDetail(id)"
    >
      <i class="fas fa-arrow-up"></i>
      New Updates
    </button>

    <div class="text-center" v-if="loading">
      <CSpinner color="info" />
    </div>
    <div class="form-row justify-content-md-center" v-else-if="detail">
      <div class="col-md-8 gallery-show" v-if="images && images.length">
        <a
          v-if="images.length > 1 && imageShow > 0"
          href="javascript:"
          class="gal-nav nav-left text-light"
          @click="imageShow--"
        >
          <i class="fas fa-chevron-left"></i>
        </a>
        <img
          v-for="(image, i) in images"
          :key="`image-${i}`"
          v-lazy="
            `${$http.defaults.baseURL}/Document/download/SipIdea$${detail.Id}/${image}`
          "
          alt=""
          class="img-fluid"
          :class="{ 'd-none': i != imageShow }"
        />
        <a
          v-if="images.length > 1 && imageShow < images.length - 1"
          href="javascript:"
          class="gal-nav nav-right text-light"
          @click="imageShow++"
        >
          <i class="fas fa-chevron-right"></i>
        </a>
      </div>
      <div :class="images && images.length ? 'col-md-4' : 'col-md-8'">
        <CCard class="mb-3" style="border-top: 3px solid #0080ff">
          <CCardHeader class="py-2" style="line-height: 2rem">
            <span class="text-dark">
              <i class="fas fa-lightbulb"></i> &nbsp;
              <strong>{{ detail.Title }}</strong>
            </span>

            <div class="d-inline-block float-right align-right">
              <div class="form-row">
                <div class="col-auto">
                  <CBadge
                    :color="$const.IDEA_STATUS_TEXT[detail.Status].color"
                    class="px-2 py-1 mr-2"
                  >
                    {{
                      $const.IDEA_STATUS_TEXT[detail.Status].text.toUpperCase()
                    }}
                  </CBadge>
                </div>
                <div
                  class="col-auto"
                  v-if="
                    (detail.Status == $const.IDEA_STATUS.New ||
                      detail.Status == $const.IDEA_STATUS.NeedsInfo) &&
                    sipUser &&
                    sipUser.UserId == detail.CreatedBy
                  "
                >
                  <CDropdown
                    class="c-header-nav-items border-left"
                    placement="bottom-end"
                    add-menu-classes="py-0"
                  >
                    <template #toggler>
                      <CButton
                        color="none"
                        size="sm"
                        class="float-right d-inline"
                        title="Options"
                        style="font-size: 1.2rem; line-height: 1rem"
                      >
                        <i class="fas fa-ellipsis"></i>
                      </CButton>
                    </template>
                    <CDropdownItem
                      class="px-2 py-1"
                      @click="
                        editing = true;
                        ideaId = id;
                      "
                    >
                      <i class="fas fa-pencil mr-2"></i> Edit post
                    </CDropdownItem>
                    <CDropdownItem
                      class="px-2 py-1 text-danger"
                      @click="deletePost(detail)"
                    >
                      <i class="fas fa-trash mr-2"></i> Delete post
                    </CDropdownItem>
                  </CDropdown>
                </div>
              </div>
            </div>
          </CCardHeader>

          <CCardBody class="px-3 py-2">
            <div class="form-row">
              <div class="col-lg-2 col-3">
                <img
                  src="/img/avatar.png"
                  alt=""
                  v-if="detail.IsAnonymous"
                  class="rounded-circle w-100"
                />
                <img
                  v-else
                  :src="`${$http.defaults.baseURL}/Document/download/Employees/${detail.CreatedByNavigation.Email}.jpg`"
                  class="rounded-circle w-100"
                  @error="replaceByDefault"
                />
              </div>
              <div class="col-lg-10 col-9">
                <h6>
                  {{
                    detail.IsAnonymous
                      ? "Anonymous"
                      : detail.CreatedByNavigation.Name
                  }}
                </h6>
                <div class="small text-muted">
                  <i class="fas fa-clock"></i>
                  {{ showDateTime(detail.UpdatedAt) }}
                  &nbsp;|&nbsp; <i class="fas fa-globe"></i>
                  {{ detail.Area.Name }}
                </div>
              </div>
            </div>
            <div class="description short py-2">
              <pre
                :class="{ 'more-info': seeMore }"
                v-html="$func.getBoldString(detail.Description)"
              ></pre>
              <span
                v-if="!seeMore"
                role="button"
                class="see-more text-muted"
                @click="seeMore = true"
              >
                <strong>...see more</strong>
              </span>
            </div>

            <div class="attached" v-if="detail.AttachedFiles">
              <ul style="list-style-type: none" class="p-0">
                <li
                  v-for="(file, i) in getFileNames(detail)"
                  :key="`file-${i}`"
                >
                  <a
                    :href="`${$http.defaults.baseURL}/Document/download/SipIdea$${detail.Id}/${file.fileName}`"
                    target="_blank"
                    class="text-dark"
                  >
                    <i
                      :class="`fas ${file.icon}`"
                      style="font-size: 1.2rem"
                    ></i>
                    {{ file.fileName }}
                  </a>
                </li>
              </ul>
            </div>

            <div
              v-if="
                [
                  $const.IDEA_STATUS.Approved,
                  $const.IDEA_STATUS.Declined,
                  $const.IDEA_STATUS.NeedsInfo,
                ].includes(detail.Status) && detail.ReasonByNavigation
              "
              :class="`small text-${
                $const.IDEA_STATUS_TEXT[detail.Status].color
              } mb-2`"
            >
              <i
                :class="`fas fa-${
                  detail.Status == $const.IDEA_STATUS.Declined
                    ? 'ban'
                    : detail.Status == $const.IDEA_STATUS.NeedsInfo
                    ? 'question'
                    : 'check-double'
                }`"
              ></i>
              {{ $const.IDEA_STATUS_TEXT[detail.Status].text }} on
              {{ $moment(detail.ReasonAt).format("DD MMM YYYY, HH:mm") }} by
              {{ detail.ReasonByNavigation.FirstName }}
              <br />
              <i class="far fa-comment"></i>
              {{ detail.Reason }}
            </div>

            <div class="form-row">
              <div class="col">
                <a
                  href="javascript:"
                  class="text-muted"
                  @click="showingVoteId = detail.Votes ? detail.Id : 0"
                >
                  <i class="far fa-thumbs-up"></i>
                  {{ detail.Votes ? detail.Votes : 0 }} likes
                </a>
              </div>
            </div>
          </CCardBody>
          <CCardFooter>
            <div class="form-row text-center">
              <div class="col">
                <a href="javascript:" class="text-muted" v-if="working">
                  <i class="fas fa-thumbs-up"></i>
                  Like
                </a>
                <a
                  v-else-if="detail.SipIdeaVotes && detail.SipIdeaVotes.length"
                  href="javascript:"
                  class="text-primary"
                  @click="unvoteIdea(detail)"
                >
                  <i class="fas fa-thumbs-up"></i>
                  Like
                </a>
                <a
                  v-else
                  href="javascript:"
                  class="text-muted"
                  @click="voteIdea(detail)"
                >
                  <i class="fas fa-thumbs-up"></i>
                  Like
                </a>
              </div>
              <div class="col">
                <a
                  href="javascript:"
                  class="text-success"
                  v-if="
                    managerId &&
                    [
                      $const.IDEA_STATUS.New,
                      $const.IDEA_STATUS.NeedsInfo,
                    ].includes(detail.Status)
                  "
                  @click="actionType = 'Approve'"
                  :class="{ 'font-weight-bold': actionType == 'Approve' }"
                >
                  <i class="fas fa-check"></i>
                  Approve
                </a>
              </div>
              <div class="col">
                <a
                  href="javascript:"
                  class="text-info"
                  v-if="managerId && detail.Status == $const.IDEA_STATUS.New"
                  @click="actionType = 'NeedsInfo'"
                  :class="{ 'font-weight-bold': actionType == 'NeedsInfo' }"
                >
                  <i class="fas fa-question"></i>
                  Needs Info
                </a>
              </div>
              <div class="col">
                <a
                  href="javascript:"
                  class="text-danger"
                  v-if="managerId && detail.Status == $const.IDEA_STATUS.New"
                  @click="actionType = 'Decline'"
                  :class="{ 'font-weight-bold': actionType == 'Decline' }"
                >
                  <i class="fas fa-ban"></i>
                  Decline
                </a>
              </div>
            </div>
            <div v-if="actionType" class="border-top pt-2 mt-2">
              <div class="form-row">
                <div class="col">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Reason"
                    v-model="reason"
                  />
                </div>
                <div class="col-auto">
                  <button
                    :class="`btn btn-sm btn-${
                      actionType == 'Approve'
                        ? 'success'
                        : 'NeedsInfo'
                        ? 'info'
                        : 'danger'
                    }`"
                    @click="doIdeaAction(detail)"
                  >
                    <i v-if="actionType == 'Approve'" class="fas fa-check"></i>
                    <i
                      v-else-if="actionType == 'Decline'"
                      class="fas fa-ban"
                    ></i>
                    <i
                      v-else-if="actionType == 'NeedsInfo'"
                      class="fas fa-question"
                    ></i>
                    {{ actionType }}
                  </button>
                </div>
                <div class="col-auto">
                  <button
                    class="btn btn-secondary btn-sm"
                    @click="
                      actionType = null;
                      reason = null;
                    "
                  >
                    <i class="fas fa-close"></i>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </CCardFooter>
        </CCard>
      </div>
    </div>

    <editPopup
      :editing.sync="editing"
      :ideaId.sync="ideaId"
      @reload="loadDetail(id)"
    />

    <votesPopup :entityId.sync="showingVoteId" />
  </div>
</template>

<script>
import editPopup from "./Popup.vue";
import votesPopup from "@/components/sipVotes.vue";

export default {
  components: {
    editPopup,
    votesPopup,
  },
  data() {
    return {
      detail: null,
      loading: false,
      seeMore: false,
      working: false,
      images: [],
      imageShow: 0,
      managerId: 0,
      actionType: null,
      reason: null,
      editing: false,
      ideaId: 0,
      showingVoteId: 0,
      hasNewUpdated: false,
    };
  },
  created: function () {
    this.$messageHub.$on("IdeaVoteChange", this.ideaVoteChange);
    this.$messageHub.$on("UpdatedNotice", this.updateNotice);
  },
  beforeDestroy: function () {
    //clean SignalR event
    this.$messageHub.$off("IdeaVoteChange", this.ideaVoteChange);
    this.$messageHub.$off("UpdatedNotice", this.updateNotice);
  },
  mounted() {
    if (this.id) {
      this.loadDetail(this.id);
    }
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    sipUser() {
      let sipUser = localStorage.getItem("sipUser");
      if (sipUser) {
        return JSON.parse(sipUser);
      } else {
        return null;
      }
    },
  },
  methods: {
    async loadDetail(id) {
      this.loading = true;

      try {
        let detail = null;
        let userId = this.sipUser ? this.sipUser.UserId : 0;
        let filter = [];
        filter.push(`Id eq ${id}`);

        let params = {
          $top: 1,
          $filter: filter.join(` and `),
          $select:
            "Id,Title,Description,AttachedFiles,UpdatedAt,CreatedBy,Status,Votes,AreaId,Reason,ReasonAt,IsAnonymous",
          $expand: `Area($select=Name),CreatedByNavigation($select=Name,Email),SipIdeaVotes($top=1;$select=Id;$filter=UserId eq ${userId}),ReasonByNavigation($select=FirstName)`,
        };

        let resp = await this.$http.get(`odata/SipIdea`, {
          params,
        });
        if (resp && resp.status == 200) {
          detail =
            resp.data.value && resp.data.value.length
              ? resp.data.value[0]
              : null;
        } else {
          detail = null;
        }
        this.detail = detail;
        this.getImageNames(detail);
        this.checkIsAreaManager(detail);
      } catch (error) {
        alert(error);
      }

      setTimeout(() => {
        this.catchEventsForHashTag();
      }, 1000);

      this.hasNewUpdated = false;
      this.loading = false;
    },

    async voteIdea(idea) {
      idea.working = true;
      try {
        let resp = await this.$http.post(`odata/SipIdeaVote`, {
          IdeaId: idea.Id,
        });
        if (resp && resp.status == 201) {
          //idea.Votes = idea.Votes ? idea.Votes + 1 : 1;
          idea.SipIdeaVotes = [{ Id: resp.data.Id }];
        }
      } catch (error) {
        console.log(error);
      }
      idea.working = false;
    },

    async unvoteIdea(idea) {
      this.working = true;
      try {
        let voteId =
          idea.SipIdeaVotes && idea.SipIdeaVotes.length
            ? idea.SipIdeaVotes[0].Id
            : 0;
        if (voteId) {
          let resp = await this.$http.delete(`odata/SipIdeaVote/${voteId}`);
          if (resp && resp.status == 204) {
            //idea.Votes = idea.Votes && idea.Votes > 0 ? idea.Votes - 1 : 0;
            idea.SipIdeaVotes = [];
          }
        }
      } catch (error) {
        console.log(error);
      }
      this.working = false;
    },

    async ideaVoteChange({ entityId, votes }) {
      let idea = this.detail;
      if (idea && idea.Id == entityId) {
        idea.Votes = votes;

        // load vote state
        this.working = true;
        try {
          let resp = await this.$http.get(`odata/SipIdeaVote`, {
            params: {
              $top: 1,
              $select: "Id",
              $filter: `IdeaId eq ${idea.Id}`,
            },
          });
          if (resp && resp.status == 200) {
            idea.SipIdeaVotes = resp.data.value;
          }
        } catch (error) {
          console.log(error);
        }
        this.working = false;
      }
    },

    getImageNames(detail) {
      let result = [];
      if (detail && detail.AttachedFiles) {
        let attachedFiles = detail.AttachedFiles;
        let files = JSON.parse(attachedFiles).filter(
          (_) =>
            _.endsWith(".jpg") ||
            _.endsWith(".jpeg") ||
            _.endsWith(".png") ||
            _.endsWith(".webp") ||
            _.endsWith(".gif") ||
            _.endsWith(".svg")
        );
        if (files && files.length) {
          result = files;
        }
      }
      this.images = result;
      this.imageShow = 0;
    },

    getFileNames(detail) {
      let result = [];
      if (detail && detail.AttachedFiles) {
        let attachedFiles = detail.AttachedFiles;
        let files = JSON.parse(attachedFiles);
        if (files && files.length) {
          result = files
            .map((_) => {
              let icon =
                _.endsWith(".xlsx") || _.endsWith(".xls")
                  ? "fa-file-excel"
                  : _.endsWith(".docx") || _.endsWith(".doc")
                  ? "fa-file-word"
                  : _.endsWith(".pdf")
                  ? "fa-file-pdf"
                  : "";
              return { icon, fileName: _ };
            })
            .filter((_) => _.icon);
        }
      }
      return result;
    },

    truncateHtmlString(trunc, len) {
      let result = "";
      if (trunc.length > len) {
        /* Truncate the content of the P, then go back to the end of the
   previous word to ensure that we don't truncate in the middle of
   a word */
        result = trunc.substring(0, len);
        result = result.replace(/\w+$/, "");

        /* Add an ellipses to the end and make it a link that expands
   the paragraph back to its original size */
        trunc +=
          '<a href="#" ' +
          'onclick="this.parentNode.innerHTML=' +
          "unescape('" +
          escape(trunc) +
          "');return false;\">" +
          "...</a>";
        return result;
      } else {
        return trunc;
      }
    },

    showDateTime(createdAt) {
      return this.$moment().diff(this.$moment(createdAt), "weeks") < 1
        ? this.$moment(createdAt).fromNow()
        : this.$moment(createdAt).format("DD MMM YYYY, HH:mm");
    },

    async checkIsAreaManager(detail) {
      let result = 0;
      if (detail && detail.AreaId) {
        let filter = [];
        filter.push(
          `(AreaId eq ${detail.AreaId} and RoleCode eq '${this.$const.SIP_ROLES.Mod}')`
        );
        filter.push(`RoleCode eq '${this.$const.SIP_ROLES.Admin}'`);
        let resp = await this.$http.get(`odata/SipUserRole`, {
          params: {
            $top: 1,
            $select: "UserId",
            $filter: filter.join(` or `),
          },
        });
        if (resp && resp.status == 200) {
          result =
            resp.data && resp.data.value.length ? resp.data.value[0].UserId : 0;
        }
      }
      this.managerId = result;
    },

    async doIdeaAction(detail) {
      if (detail) {
        if (!this.reason) {
          alert("Reason is required!");
          return;
        }

        let actionType = this.actionType;
        await this.$http.patch(`odata/SipIdea/${detail.Id}`, {
          Status:
            actionType == "Approve"
              ? this.$const.IDEA_STATUS.Approved
              : actionType == "NeedsInfo"
              ? this.$const.IDEA_STATUS.NeedsInfo
              : this.$const.IDEA_STATUS.Declined,
          Reason: this.reason,
          ReasonAt: new Date().toISOString(),
          ReasonBy: this.managerId,
        });

        this.reason = null;
        this.actionType = null;
        this.loadDetail(this.id);
      }
    },

    replaceByDefault(e) {
      e.target.src = "/img/avatar.png";
    },

    catchEventsForHashTag() {
      let tags = this.$el.querySelectorAll(".description pre a");
      if (tags && tags.length) {
        tags.forEach((tag) => {
          tag.addEventListener("click", (e) => {
            let text = e.target.innerText;
            location.href = `/Idea?keywords=${encodeURIComponent(text)}`;
          });
        });
      }
    },

    updateNotice({ entityId, entityType }) {
      if (entityType == this.$const.ENTITY_NAMES.Idea && this.id == entityId) {
        this.hasNewUpdated = true;
      }
    },

    async deletePost(item) {
      if (confirm("Do you really want to delete this idea?")) {
        if (item.Status == this.$const.IDEA_STATUS.New) {
          // remove files
          let frmData = new FormData();
          frmData.append("Id", item.Id);
          frmData.append("IsReplaced", true);
          frmData.append("EntityName", "SipIdea");

          await this.$http.post("/SipFile", frmData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          let resp = await this.$http.delete(`/odata/SipIdea/${item.Id}`);
          if (resp && resp.status == 204) {
            // reload
            this.$router.push({ name: "Idea" });
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.gallery-show {
  position: relative;
  background: #000;
  height: calc(100vh - 6rem);
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    max-height: 100%;
  }

  .gal-nav {
    position: absolute;
    font-size: 2rem;
    top: calc(50% - 1rem);
    text-shadow: 1px 2px #636f83;
  }
  .nav-left {
    left: 1rem;
  }

  .nav-right {
    right: 1rem;
  }
}

.description {
  position: relative;

  pre {
    white-space: pre-line;
    font-family: inherit;
    font-size: 14px;
    width: 100%;
    margin-bottom: 0;
    max-height: 4rem;
    overflow: hidden;

    &.more-info {
      max-height: unset;
    }
  }

  .see-more {
    position: absolute;
    bottom: 0.5rem;
    right: 0;
    overflow: hidden;
    background: #fff;
    padding-left: 10px;
  }
}
</style>
